import { useState } from 'react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';

interface ResetPasswordModalProps {
  open: boolean;
  userName: string;
  /** Role of the target user whose password is being reset */
  targetRole?: string;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (password: string, currentPassword: string) => void;
}

export function ResetPasswordModal({ open, userName, targetRole, loading, onClose, onSubmit }: ResetPasswordModalProps) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  // Current password required only when resetting Owner/HR Admin passwords.
  // For employees, no current password needed — Owner can reset directly.
  const targetRoleLower = (targetRole || '').toLowerCase();
  const requireCurrentPassword = targetRoleLower === 'superadmin' || targetRoleLower === 'admin';

  // Clear fields each time the modal opens
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setCurrentPassword('');
      setPassword('');
      setError('');
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (requireCurrentPassword && !currentPassword.trim()) {
      setError('Your current password is required.');
      return;
    }
    if (password.length < 6) {
      setError('New password must be at least 6 characters.');
      return;
    }
    onSubmit(password, requireCurrentPassword ? currentPassword : '');
  };

  const canSubmit = password.length >= 6 && (!requireCurrentPassword || currentPassword.length > 0);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Reset Password — ${userName}`}
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="reset-pwd-form"
            loading={loading}
            disabled={!canSubmit}
          >
            Reset Password
          </Button>
        </>
      }
    >
      <form id="reset-pwd-form" onSubmit={handleSubmit} className="space-y-3">
        {error && (
          <div className="rounded-lg bg-danger/10 px-3 py-2 text-sm text-danger-text dark:bg-danger dark:text-danger-text">
            {error}
          </div>
        )}
        {requireCurrentPassword && (
          <Input
            label="Current Password"
            name="current-password"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            placeholder="Enter your current password"
            autoComplete="current-password"
            required
          />
        )}
        <Input
          label="New Password"
          name="reset-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="At least 6 characters"
          required
        />
      </form>
    </Modal>
  );
}
